import { SOLD_PAGE_ENABLED } from "config";

import { addApolloState, createApolloClient } from "../../lib/apollo";
import { getTranslationsWithSubsets } from "../../lib/getTranslations";
import {
  applyAnonymousHtmlCache,
  readAnonymousHtmlCacheConfig,
} from "@/common/utils/anonymous-html-cache.utils";
import { resolveAskingPrice } from "@/common/utils/asking-price.utils";
import { isNotModifiedSince } from "@/common/utils/conditional-request.utils";
import { isSessionRejected } from "@/common/utils/graphql-error.utils";
import { buildProductSchema } from "@/common/utils/json-ld.utils";
import { enhanceServerSidePropsContext } from "@/common/utils/next.utils";
import {
  ALTERNATIVES_LEVEL,
  PRICE_BAND_OUTCOME,
  buildAlertFilters,
  buildAlertTitle,
  buildCategoryFallback,
  describeAlternativesOutcome,
  describeMatchedCriteria,
  extractAlternativeCriteria,
  findSoldAlternatives,
  resolveCategoryTitle,
} from "@/common/utils/sold-alternatives.utils";
import { redactSoldPageProps } from "@/common/utils/sold-price.utils";
import {
  AVAILABILITY,
  UNAVAILABLE_VARIANT,
  buildFallbackListingUrl,
  resolveProductAvailability,
  resolveWithdrawnProductAvailability,
} from "@/common/utils/sold-product.utils";
import { parseRelativePath } from "@/common/utils/url.utils";
import { withIncludeWithdrawn } from "@/common/utils/withdrawn-product-query.utils";
import {
  displayCurrencyForPage,
  pageCacheConfig,
} from "@/features/display-currency/server/display-currency.server";
import {
  PRO_PROJECTS_NAMESPACE,
  PRO_PROJECTS_PDP_LOKALISE_KEYS,
} from "@/features/pro-buyer-projects/static/pro-buyer-projects.static";
import {
  US_DELIVERY_CHECKOUT_NAMESPACE,
  US_DELIVERY_PDP_CHECKOUT_KEYS,
} from "@/features/us-delivery/static/us-delivery.static";
import { usMarketVariables } from "@/features/us-market/us-shippable";
import { toContentLocale } from "@/i18n/config";
import getFallbackListingPageQuery from "@whoppah/apollo/query/getFallbackListingPage.graphql";
import productBySlugQuery from "@whoppah/apollo/query/productBySlug.graphql";
import searchProductsQuery from "@whoppah/apollo/query/searchProducts.graphql";
import { HeaderLanguageSwitcherProvider } from "@whoppah/components/Header/HeaderLanguageSwitcherContext";
import MainLayout from "@whoppah/components/Layouts/MainLayout";
import { ProductDetailsContainer } from "@whoppah/components/ProductDetail/ProductDetailsContainer";
import { SoftSoldProduct } from "@whoppah/components/ProductDetail/SoftSoldProduct";

const ProductDetailsPage = props => {
  // Soft-Sold and Recently Gone phases render a different page for the
  // same URL; see `getServerSideProps` below and WHO-4840.
  if (props.softSold) {
    return (
      <SoftSoldProduct
        product={props.product}
        similarProducts={props.similarProducts}
        variant={props.unavailableVariant}
        robots={props.robots}
        alternativesMeta={props.alternativesMeta}
        previewMode={props.previewMode}
      />
    );
  }

  return <ProductDetailsContainer {...props} />;
};

ProductDetailsPage.getLayout = (page, props) => {
  return (
    <HeaderLanguageSwitcherProvider
      value={{ urls: props.languageSwitcherUrls }}
    >
      <MainLayout>{page}</MainLayout>
    </HeaderLanguageSwitcherProvider>
  );
};

const getProductUrl = (slug, locale, query) => {
  /** @type {Record<import("next/router").Locale, string>} */
  const pdpPath = {
    en: "/products",
    nl: "/nl/producten",
    de: "/de/produkte",
    fr: "/fr/produits",
    es: "/es/productos",
    it: "/it/prodotti",
    // `en-us` is English content under its own prefix (WHO-5766).
    "en-us": "/en-us/products",
  };

  // Construct url with slug returned from api. The origin is sliced off again
  // below, so this only needs a parse base, not the canonical host (WHO-5133).
  const correctUrl = parseRelativePath(`${pdpPath[locale]}/${slug}`);

  // Add search params to the url object
  for (const [key, value] of Object.entries(query)) {
    // slug and nextInternalLocale are not search param but exists in query (nextjs shenanigans)
    if (!["slug", "nextInternalLocale"].includes(key)) {
      correctUrl.searchParams.set(key, value);
    }
  }

  // Remove origin from href
  return correctUrl.href.slice(correctUrl.origin.length);
};

const buildLanguageSwitcherUrls = (product, locales) =>
  locales.reduce((urls, locale) => {
    urls[locale] = getProductUrl(
      product[`slug_${toContentLocale(locale)}`],
      locale,
      {}
    );
    return urls;
  }, {});

/*
 * Every namespace here is serialised into the page HTML (`__NEXT_DATA__`), on
 * top of the shell namespaces `lib/getTranslations` adds to every page. Only
 * namespaces something in the PDP render tree reads belong here, including
 * the modals that can open from it (bidding, shipping options, signup).
 *
 * Removed after an audit of every `t("ns:...")` / `useTranslation("ns")` in
 * `src/` and `pages/` (see `__tests__/lib/translation-namespaces.spec.js`):
 * `404` (read by `pages/404`, `pages/500` and `src/errors/*` only; a missing
 * product redirects, it never renders those), `additionalinfo` (read by the
 * create-ad wizard and edit-ad only), `about` (read by `pages/about` only),
 * `buyer-protection` (read by `BuyerProtectionModal`, the glossary and
 * how-it-works/buying only; the PDP's buyer-protection USP reads `pdp`), and
 * `boost-ad`, which the shell list already carries.
 */
export const PRODUCT_PAGE_NAMESPACES = [
  "pdp",
  "color",
  "material",
  "style",
  "subject",
  "usagesign",
  "bidding-modal",
  "shipping-options",
  "signup",
];

/*
 * Namespaces the PDP reads only a few keys of: only those keys ship. WHO-5959
 * F17: the reserve modal's project field and "+ Nieuw project" dialog read
 * `account` keys; without them they rendered the English fallbacks. WHO-6509:
 * the US delivery quote reads one `checkout` key.
 */
export const PRODUCT_PAGE_NAMESPACE_SUBSETS = {
  [PRO_PROJECTS_NAMESPACE]: PRO_PROJECTS_PDP_LOKALISE_KEYS,
  // WHO-6509: "You pay in euros…" under a converted US delivery quote fee.
  [US_DELIVERY_CHECKOUT_NAMESPACE]: US_DELIVERY_PDP_CHECKOUT_KEYS,
};

// The gateway refused the viewer's token. Says nothing about the product.
const SESSION_REJECTED = Symbol("session rejected");

// try to cath error if APi call fails for some reason.
// If it fails we set product to null and redirect to fallback listing page
const tryGetProduct = async (client, slug, locale) => {
  try {
    const { data } = await client.query({
      query: productBySlugQuery,
      // `usMarket`: the same variables as `useProductsBySlug`, so the page reads this cache entry.
      variables: {
        slug: slug,
        lang: toContentLocale(locale).toUpperCase(),
        ...usMarketVariables(locale),
      },
      context: {
        timeout: 2000,
      },
    });

    return data?.productBySlug ?? null;
  } catch (error) {
    if (isSessionRejected(error)) {
      return SESSION_REJECTED;
    }

    console.error(error);
    // `undefined`, not null: the lookup failed, so nothing is known about
    // the product. Only a successful "not found" may trigger the withdrawn
    // lookup below, or a timeout would show a live item as gone (WHO-6094).
    return undefined;
  }
};

/*
 * WHO-6199. The same product in the address's display currency, asked for
 * only to fill the Apollo cache the page ships with: the browser's first
 * `useProductsBySlug` asks for the same variables, finds them, and paints the
 * converted prices straight away. Everything this page decides or renders on
 * the server (redirects, availability, JSON-LD) reads the euro product above,
 * so structured data stays in euros. The converted fields sit next to the euro
 * ones in the cache, keyed by their `currency` argument. A failure only costs
 * the browser a refetch.
 */
const prefetchProductInCurrency = async (client, slug, locale, currency) => {
  try {
    await client.query({
      query: productBySlugQuery,
      variables: {
        slug: slug,
        lang: toContentLocale(locale).toUpperCase(),
        currency,
        ...usMarketVariables(locale),
      },
      context: {
        timeout: 2000,
      },
    });
  } catch (error) {
    console.warn("products/[slug]: converted prefetch failed", error?.message);
  }
};

// WHO-6094. Second lookup, only when the first found nothing: a listing
// the seller deleted is a soft delete the gateway hides unless asked. A
// gateway that predates `includeWithdrawn` rejects the request, which lands
// here as null and keeps today's redirect. See `withIncludeWithdrawn`.
const productBySlugIncludingWithdrawnQuery =
  withIncludeWithdrawn(productBySlugQuery);

const tryGetWithdrawnProduct = async (client, slug, locale) => {
  try {
    const { data } = await client.query({
      query: productBySlugIncludingWithdrawnQuery,
      variables: { slug: slug, lang: toContentLocale(locale).toUpperCase() },
      fetchPolicy: "no-cache",
      context: {
        timeout: 2000,
      },
    });

    return data?.productBySlug ?? null;
  } catch (error) {
    console.warn("withdrawn product lookup failed", slug, error?.message);
    return null;
  }
};

// The gateway resolves a missing translation to "" rather than null, so
// the fallback page can come back with an empty url. Never let a failure
// here take the page down: the caller has a guaranteed default.
const tryGetFallbackListingPageUrl = async (client, slug, locale) => {
  try {
    const { data } = await client.query({
      query: getFallbackListingPageQuery,
      variables: { slug: slug, lang: toContentLocale(locale).toUpperCase() },
      context: {
        timeout: 2000,
      },
    });

    return data?.getFallbackListingPage?.url;
  } catch (error) {
    console.error("products/[slug]:getFallbackListingPage", error);
    return null;
  }
};

/**
 * Comparable alternatives for a sold listing.
 *
 * The rules live in `sold-alternatives.utils.ts`; this function is only
 * the gateway adapter for them. It supplies the `search` callback the
 * ladder calls once per rung and returns both the alternatives and the
 * rung that produced them, so the page can record which level fired.
 *
 * A failed search degrades to "no alternatives", which the module renders
 * as a link to the category page rather than an empty grid. A sold page
 * must never 500 on a crawler because the search index was slow.
 */
const tryGetAlternatives = async (client, product, locale) => {
  const search = async (filters, limit) => {
    const { data } = await client.query({
      query: searchProductsQuery,
      variables: {
        lang: toContentLocale(locale).toUpperCase(),
        input: {
          filters,
          pagination: { page: 1, limit },
        },
      },
      context: {
        timeout: 2000,
      },
    });

    return {
      items: data?.searchProducts?.items ?? [],
      count: data?.searchProducts?.pagination?.count ?? 0,
    };
  };

  try {
    return await findSoldAlternatives({ product, search });
  } catch (error) {
    console.error("products/[slug]:searchProducts", error);

    // Degrade to the same shape an empty ladder returns, keeping the
    // category link so the page still offers a way forward.
    return {
      level: ALTERNATIVES_LEVEL.L5,
      alternatives: [],
      filters: [],
      priceBand: PRICE_BAND_OUTCOME.NotApplicable,
      matchCount: 0,
      categoryFallback: buildCategoryFallback(
        extractAlternativeCriteria(product)
      ),
    };
  }
};

// Next refuses to serialize `undefined` in props. Apollo results are
// JSON-safe in practice, but this page must never 500 on a crawler.
const toSerializableProps = value => JSON.parse(JSON.stringify(value ?? null));

/*
 * The realised sale price is stripped out of every sold-page payload by
 * `redactSoldPageProps`, which covers the serialized product, the
 * alternatives and the inlined Apollo cache. The rules and the reasoning
 * live in `sold-price.utils.ts`, which is pure so they can be tested.
 */

/**
 * Query flag that renders the sold treatment for a product that has not
 * sold, so the page can be reviewed on a Vercel preview deployment.
 *
 * We cannot demo this on a genuinely sold listing: the whole point of the
 * feature is that sold listings are rare, short-lived and not something
 * you can conjure on demand in an environment pointed at production data.
 */
const SOLD_PREVIEW_PARAM = "preview_sold";

/**
 * The guard, in two independent parts. Both must hold.
 *
 *  1. `VERCEL_ENV !== "production"`. Vercel sets this on every
 *     deployment: `production` for the live domain, `preview` for a PR
 *     deployment, and it is absent locally. So PR previews and `next dev`
 *     can render the treatment and www.whoppah.com cannot, whatever the
 *     query string says. `NODE_ENV` would be useless here, because a
 *     preview deployment is a production build and reports "production".
 *  2. An explicit `?preview_sold=1`. Without it, preview deployments
 *     behave exactly like production, so what a reviewer sees on a
 *     preview URL is the real page unless they deliberately ask for the
 *     sold treatment.
 *
 * The rendered page is `noindex,follow` in any case, and the response is
 * additionally sent with `X-Robots-Tag: noindex` so a preview deployment
 * cannot leak a fake sold page into an index.
 */
const isSoldPreviewRequested = query =>
  process.env.VERCEL_ENV !== "production" &&
  String(query?.[SOLD_PREVIEW_PARAM] ?? "") === "1";

/**
 * Props for the sold treatment. Shared by the real Soft-Sold/Recently
 * Gone path and by the preview flag, so a reviewer on a preview URL is
 * looking at the same code path a real sold listing goes through, not a
 * lookalike that can drift away from it.
 */
const buildSoftSoldProps = async (
  client,
  product,
  locale,
  locales,
  {
    previewMode = false,
    variant = UNAVAILABLE_VARIANT.Sold,
    robots = "noindex,follow",
  } = {}
) => {
  const alternatives = await tryGetAlternatives(client, product, locale);

  // One line per sold render. This is how we find out which rung of the
  // ladder actually fires on live traffic, which is the only way to judge
  // whether the criteria are set correctly. The client fires the matching
  // `sold_alternatives_shown` analytics event; this log is the server-side
  // record that survives ad blockers.
  console.info(describeAlternativesOutcome(product.slug, alternatives));

  return {
    props: {
      softSold: true,
      previewMode,
      unavailableVariant: variant,
      robots,
      alternativesMeta: {
        level: alternatives.level,
        priceBand: alternatives.priceBand,
        matchCount: alternatives.matchCount,
        // What the page is allowed to say it matched on.
        matchedCriteria: describeMatchedCriteria(alternatives.filters),
        categoryFallback: toSerializableProps(alternatives.categoryFallback),
        // Always L0, whichever rung actually served the page: an alert is
        // a promise about a genuinely comparable piece.
        alertFilters: buildAlertFilters(product),
        alertTitle: buildAlertTitle(product),
        // The number every card is anchored against. Asking, never
        // realised: `sold_at` is redacted out of this payload entirely.
        soldAskingMoney: toSerializableProps(resolveAskingPrice(product)),
        // Where "see all N comparable X" points. Computed on every rung,
        // unlike `categoryFallback`, which the ladder only fills in when it
        // dead-ends at L5.
        browseAll: toSerializableProps(
          buildCategoryFallback(extractAlternativeCriteria(product))
        ),
        categoryTitle: resolveCategoryTitle(product),
      },
      product: toSerializableProps(product),
      similarProducts: toSerializableProps(alternatives.alternatives),
      languageSwitcherUrls: buildLanguageSwitcherUrls(product, locales),
      ...(await getTranslationsWithSubsets(
        locale,
        PRODUCT_PAGE_NAMESPACES,
        PRODUCT_PAGE_NAMESPACE_SUBSETS
      )),
    },
  };
};

export const getServerSideProps = async context => {
  const {
    defaultLocale,
    locale,
    locales,
    params,
    res,
    req,
    query,
    token,
    userId,
    userIp,
    deviceId,
  } = enhanceServerSidePropsContext(context);

  // WHO-5684: with the anonymous-HTML cache on, the page is rendered without
  // the viewer for everyone, so the CDN can serve one copy per URL. Hearts
  // and own-listing state are picked up client-side after hydration. See
  // `anonymous-html-cache.utils.ts`.
  const anonymousHtmlCache = readAnonymousHtmlCacheConfig();
  const viewer = anonymousHtmlCache ? {} : { token, userId };

  let client = createApolloClient({ ...viewer, userIp, deviceId });

  // WHO-6199: `?currency=GBP` renders the prices in pounds, behind the gate.
  // The edge middleware has already matched the address to the visitor's own
  // saved choice; see `currency-url.ts`. The euro product doesn't wait for
  // the gate check: both start at once, and only the converted prefetch
  // needs the gate's answer.
  const displayCurrencyAsked = displayCurrencyForPage({ query, locale });
  const prefetchInCurrency = target =>
    displayCurrencyAsked.then(async currency => {
      if (currency) {
        await prefetchProductInCurrency(target, params.slug, locale, currency);
      }

      return currency;
    });

  let [liveProduct, displayCurrency] = await Promise.all([
    tryGetProduct(client, params.slug, locale),
    prefetchInCurrency(client),
  ]);

  // WHO-6201: an expired, revoked or malformed token cookie makes the
  // gateway answer 401 before it looks at the product, which used to land
  // here as "lookup failed" and 308 a live listing to a category page. Ask
  // again as a guest; the client's `useMe` gets the same 401 and logs out.
  // The converted prefetch is asked again too: the page ships the guest
  // client's cache.
  if (liveProduct === SESSION_REJECTED) {
    client = createApolloClient({ userIp, deviceId });
    [liveProduct, displayCurrency] = await Promise.all([
      tryGetProduct(client, params.slug, locale),
      prefetchInCurrency(client),
    ]);
  }

  // Only a product the first lookup answered "not found" for can be
  // withdrawn; a live product, or a failed lookup, never takes the second
  // round trip.
  const withdrawnProduct =
    SOLD_PAGE_ENABLED && liveProduct === null
      ? await tryGetWithdrawnProduct(client, params.slug, locale)
      : null;

  const product = liveProduct ?? withdrawnProduct;

  // `?preview_sold=1` on any live product renders the sold treatment, so
  // the page can be reviewed without waiting for something to sell. Never
  // reachable on the production deployment; see `isSoldPreviewRequested`.
  // Placed before the `gone` branch so it works on a normal, live PDP.
  if (liveProduct && !liveProduct.gone && isSoldPreviewRequested(query)) {
    res.setHeader("X-Robots-Tag", "noindex");
    res.setHeader("Cache-Control", "private, no-store");

    return redactSoldPageProps(
      addApolloState(
        client,
        await buildSoftSoldProps(client, product, locale, locales, {
          previewMode: true,
        })
      )
    );
  }

  // WHO-5240. The lifecycle used to be gated on `product.gone`, which is
  // not an availability signal: the nightly `set_products_gone` task
  // sets it only for COMPLETED auctions whose `end_date` is over six
  // months old. Measured on the production gateway on 2026-08-06, three
  // genuinely sold listings (one RESERVED, two COMPLETED) all reported
  // `gone: false`, so none of them ever entered this branch, and BANNED,
  // EXPIRED and CANCELED listings rendered as normal, buyable pages
  // indefinitely.
  //
  // The trigger is now the auction state, resolved in one place by
  // `resolveProductAvailability`:
  //
  //   PUBLISHED / COMING_SOON / BID_ONLY  200  normal page, untouched
  //   RESERVED                            200  pending page, index kept
  //   COMPLETED 0-365d                    200  sold page, index,follow
  //   COMPLETED, `gone` or 365d+          301  listing page, never `/`
  //   BANNED/EXPIRED/CANCELED, published  200  neutral page, noindex
  //   BANNED pre-publication, DRAFT       404
  //
  // `gone` survives as the backend's six-month archival boundary, but it
  // is no longer the only way into the final 301: `set_products_gone`
  // keys on `auction.end_date`, so a COMPLETED auction without one is
  // never flagged, and those are the majority in production. The 365-day
  // ceiling in `resolveProductAvailability` covers them.
  //
  // With the flag off, behaviour is exactly what it is today: `gone`
  // redirects, everything else renders the normal page.
  //
  // WHO-6094: a listing the seller deleted only arrives through the
  // second lookup, and gets the same page with "no longer available".
  const availability =
    !SOLD_PAGE_ENABLED || !product
      ? null
      : withdrawnProduct
      ? resolveWithdrawnProductAvailability(withdrawnProduct)
      : resolveProductAvailability(product);

  // Never public, so there is nothing to preserve and no URL to keep
  // alive. A redirect would imply the listing once existed publicly.
  if (availability?.availability === AVAILABILITY.NotFound) {
    return { notFound: true };
  }

  const rendersUnavailablePage = availability?.render === "unavailable";

  // Archived is reached either by the backend's `gone` flag or by the
  // 365-day ceiling in `resolveProductAvailability`, and the second route
  // carries `gone: false`. Testing the decision rather than the flag is
  // what makes that route reach the redirect below; keyed on `gone` alone
  // it fell through to the normal, buyable PDP.
  const redirectsToListingPage =
    availability?.availability === AVAILABILITY.Archived;

  if (
    !product ||
    product.gone ||
    rendersUnavailablePage ||
    redirectsToListingPage
  ) {
    if (rendersUnavailablePage) {
      // 200 for every listing that renders the unavailable page. The
      // 410 that used to follow a 90-day-old sale is gone: those URLs
      // are the entry point for the alternatives, and a 410 deindexes
      // them permanently. See `resolveProductAvailability`.
      res.statusCode = availability.status;

      res.setHeader("Cache-Control", "public, max-age=0, must-revalidate");
      res.setHeader(
        "Vercel-CDN-Cache-Control",
        "public, s-maxage=300, stale-while-revalidate=300"
      );

      return redactSoldPageProps(
        addApolloState(
          client,
          await buildSoftSoldProps(client, product, locale, locales, {
            variant: availability.variant,
            robots: availability.robots,
          })
        )
      );
    }

    // Resolve the listing page from the product's own slug, not from the
    // slug in the URL.
    //
    // This is the second defect in WHO-5240. `getFallbackListingPage`
    // parses the slug it is given, so the identifier-suffixed form that
    // the CMS links to and buyers bookmark did not match a category and
    // degraded to the "New in" page, while the canonical form resolved
    // correctly. Measured on the production gateway on 2026-08-06:
    //
    //   italiaanse-design-vloerlamp-FG23QC4T7Y4F -> "new"
    //   italiaanse-design-vloerlamp              -> "lighting/floor-lights"
    //
    // Both URL forms resolve to the same product, so passing the
    // product's slug makes both follow the same policy. `params.slug`
    // remains the fallback for the case where nothing resolved at all.
    const listingPageUrl = await tryGetFallbackListingPageUrl(
      client,
      product?.slug ?? params.slug,
      locale
    );

    return {
      redirect: {
        // Ordered by how specific the target is.
        //
        // WHO-4840 asks for `relatedListingPages[0]` first, but that
        // resolver returns the brand-only page (`brands/de-sede`) while
        // `getFallbackListingPage` cascades brand x category first
        // (`brands/de-sede/stools`). Checked against four live products
        // and it held every time, so the existing call stays in front:
        // it sends the visitor somewhere closer to what they wanted.
        //
        // `buildFallbackListingUrl` guarantees a non-empty, locale
        // correct path, so this can no longer degrade to `/` or to
        // `/<locale>/undefined` the way the old template literal did.
        destination: buildFallbackListingUrl(
          [listingPageUrl, product?.relatedListingPages?.[0]?.url],
          locale,
          defaultLocale
        ),
        permanent: true,
      },
    };
  }

  // If the product.slug doesn't match params.slug then we might have found
  // the product using our fallback methods. We need to do a permanent redirect to
  // the latest url in this case.
  if (product.slug !== params.slug) {
    return {
      redirect: {
        destination: getProductUrl(product.slug, locale, query),
        permanent: true,
      },
    };
  }

  // When browsers send an `if-modified-since` header they hold the page in
  // their cache. If neither the product nor its auction changed after that
  // moment we answer 304 (Not Modified) with no body and end the response
  // ourselves, so Next.js skips rendering and the browser shows its cached
  // copy. A listing that DID change after that moment is rendered in full.
  // PS - This also helps us manage our seo crawl budget.
  const product_updated = product?.updated;
  const auction_updated = product?.auction?.updated;

  const updated =
    product_updated > auction_updated ? product_updated : auction_updated;

  const lastModified = new Date(updated);

  res.setHeader("Last-Modified", lastModified.toUTCString());

  // WHO-6199: a `?currency=` address rendered without its currency (Statsig
  // didn't answer in time) is cached only briefly; see `pageCacheConfig`.
  if (anonymousHtmlCache) {
    applyAnonymousHtmlCache(
      res,
      pageCacheConfig(anonymousHtmlCache, query, displayCurrency)
    );
  } else if (token || userId) {
    res.setHeader("Cache-Control", "private, no-cache");
  } else {
    const cdnCache = pageCacheConfig(
      { sMaxAge: 300, staleWhileRevalidate: 300 },
      query,
      displayCurrency
    );

    res.setHeader("Cache-Control", "public, max-age=0, must-revalidate");
    res.setHeader(
      "Vercel-CDN-Cache-Control",
      `public, s-maxage=${cdnCache.sMaxAge}, stale-while-revalidate=${cdnCache.staleWhileRevalidate}`
    );
    res.setHeader("Vary", "Cookie");
  }

  // A converted price moves with the exchange rate, which `Last-Modified`
  // knows nothing about, so a currency address is always rendered in full.
  if (
    !displayCurrency &&
    isNotModifiedSince(lastModified, req.headers["if-modified-since"])
  ) {
    res.statusCode = 304;
    res.end();
    return { props: {} };
  }

  // const statsigOptions = await getStatsigProps({ userId, deviceId });

  return addApolloState(client, {
    props: {
      // The Product schema is built here, from the product this function
      // already fetched, and rendered by the container from this prop. The
      // container used to build it from `useProductsBySlug`, which on the
      // server means reading the hydrated Apollo cache during render: it
      // works only while the cache diff is complete, and it disappears
      // silently, with no error, the moment it is not. Crawlers read the
      // initial HTML, so the schema must not depend on that.
      productJsonLd: toSerializableProps(
        buildProductSchema(product, locale, defaultLocale)
      ),
      // Tells the container the HTML carries no viewer data, so a signed-in
      // visitor's client re-reads the listing with their token.
      anonymousHtml: Boolean(anonymousHtmlCache),
      // WHO-6199: what `_app` seeds the display currency with, so the first
      // render in the browser asks for the prices the server fetched.
      displayCurrency: displayCurrency ?? null,
      languageSwitcherUrls: buildLanguageSwitcherUrls(product, locales),
      ...(await getTranslationsWithSubsets(
        locale,
        PRODUCT_PAGE_NAMESPACES,
        PRODUCT_PAGE_NAMESPACE_SUBSETS
      )),
      // ...statsigOptions,
    },
  });
};

export default ProductDetailsPage;
